/**
 * Bad-note ghosts of judged play (W1-06; R§5.5, R§8 item 7, S§3.2, A§7.5).
 *
 * In real play Phira draws a note judged Bad as a ghost: the note's NON-MH texture tinted rgb(108, 67, 67), its
 * alpha falling linearly from 1 to 0 over 0.5 s after the judgement. The ghost stays where the note was when it was
 * judged: it is placed with the note placement of R§4.3 (line state, controls, incline, size, mirroring) evaluated
 * at the judgement time and does not follow the line afterwards. The ghosts belong to the judge, not to the line
 * draw loop, so the line rules of R§4.5 (UI mover, line alpha < 0, cover, culling) do not apply to them; the
 * editor-only preview filters (hidden / solo lines, dim) do. Autoplay never judges Bad, so nothing is drawn there.
 *
 * `FrameDrawList` has no slot for them (change request CR-W1-06-7): they are appended to the note batch as trailing
 * buckets with kind order BAD_GHOST_KIND_ORDER (after every note kind), slot rank ascending, judgement order inside
 * a bucket, and note id -1 (a fading ghost is not a hit target; `pickNote` skips negative ids). Phira draws them
 * after the non-global effects; inside the note batch they are drawn before them, which differs only while a
 * non-global extra.json effect is active.
 */

import { Search } from '../../math/search';
import type { NoteId } from '../../model/types';
import type { EngineCompileContext, FrameOptions } from '../types';
import { LIMIT_BAD } from './constants';
import { frameEnv, makeLineEnv, NoteEval, setLineEnv, type LineEnv } from './env';
import { colorByte } from './geometry';
import { evalNote } from './noteGeom';
import { cacheOf, globalOf, lineRt, realNoteIndex } from './store';
import { dimMultiplier, lineFilteredOut } from './visibility';

/** Ghost tint (S§3.2): rgb(108, 67, 67). */
export const BAD_GHOST_RGB: readonly [number, number, number] = [108, 67, 67];

/** Seconds over which a ghost fades from alpha 1 to 0 (R§5.5). */
export const BAD_GHOST_DURATION = 0.5;

/** Kind order of the ghost buckets in NoteQuadBatch: after Hold 0, Drag 1, Tap 2 and Flick 3. */
export const BAD_GHOST_KIND_ORDER = 4;

/**
 * Whether a NoteQuadBatch bucket holds Bad-note ghosts (CR-W1-06-7). Its quads carry note id -1: consumers that map
 * `NoteQuadBatch.note` back to notes (selection outlines, overlays, hit-testing) skip these buckets, renderers draw
 * them like any other bucket (slot texture times vertex colour).
 */
export function isBadGhostBucket(bucket: { readonly kindOrder: number }): boolean {
  return bucket.kindOrder === BAD_GHOST_KIND_ORDER;
}

/** Alpha of a ghost `age` seconds after its judgement (1 at the judgement, 0 from BAD_GHOST_DURATION on). */
export function badGhostAlpha(age: number): number {
  if (!(age >= 0) || age >= BAD_GHOST_DURATION) return 0;
  return 1 - age / BAD_GHOST_DURATION;
}

/** Receives ghost quads (implemented by the quad sink of quads.ts). */
export interface BadGhostSink {
  /** Pushes quad `q` of `ev` (corners, UV and slot) with the colour bytes already set on `ev`. */
  pushGhost(ev: NoteEval, q: number): void;
}

interface Scratch {
  readonly le: LineEnv;
  readonly ev: NoteEval;
}

/**
 * Pushes the ghosts of every real non-hold note judged Bad at most BAD_GHOST_DURATION before t (judged play only;
 * a no-op in autoplay). A Bad judgement lies within LIMIT_BAD of the note time, so only the notes with a time in
 * [t - BAD_GHOST_DURATION - LIMIT_BAD, t + LIMIT_BAD] are asked.
 */
export function pushBadGhosts(
  ctx: EngineCompileContext,
  t: number,
  opts: FrameOptions,
  ar: number,
  sink: BadGhostSink,
): void {
  const judge = opts.judge;
  if (judge === null) return;
  const idx = realNoteIndex(ctx);
  const g = globalOf(ctx);
  const { le, ev } = cacheOf<Scratch>(g, 'badGhosts', () => ({ le: makeLineEnv(), ev: new NoteEval() }));
  // Ghosts use the normal (non-MH) texture and width whatever the double-hint setting.
  const player = opts.player.doubleHint ? { ...opts.player, doubleHint: false } : opts.player;
  const hi = t + LIMIT_BAD;
  for (let k = Search.lowerBound(idx.time, t - BAD_GHOST_DURATION - LIMIT_BAD); k < idx.n; k++) {
    if ((idx.time[k] ?? Infinity) > hi) break;
    const id = (idx.id[k] ?? -1) as NoteId;
    const st = judge.status(id);
    if (st?.grade !== 'bad') continue;
    const alpha = st.at <= t ? badGhostAlpha(t - st.at) : 0;
    if (!(alpha > 0)) continue;
    const line = g.noteLine.get(id);
    if (line === undefined || lineFilteredOut(opts.filters, line)) continue;
    const rt = lineRt(ctx, line);
    const i = rt.indexOf.get(id);
    if (i === undefined || rt.isHold(i)) continue;
    const s = ctx.lineState(line, st.at);
    setLineEnv(le, rt, s.m, s.height, s.inclineSin, 1);
    const fe = frameEnv(st.at, ar, 1, player, opts.skin, judge, ctx.pkg);
    evalNote(ctx, fe, le, i, true, ev);
    if (ev.quads === 0) continue;
    ev.r = BAD_GHOST_RGB[0];
    ev.g = BAD_GHOST_RGB[1];
    ev.b = BAD_GHOST_RGB[2];
    ev.alpha = alpha * dimMultiplier(opts.filters, line);
    ev.alphaByte = colorByte(ev.alpha);
    sink.pushGhost(ev, 0);
  }
}
